import { Box, Button, Stack, TextField, useTheme } from "@mui/material";
import { MinusIcon, PlusIcon } from "components/icons";
import React from "react";

export const DialerInput = ({
  value,
  onValueChange,
  disabled = false,
  maxValue,
  minValue = 0,
  isInteger = true,
}: {
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  maxValue?: number;
  minValue?: number;
  isInteger?: boolean;
}) => {
  const theme = useTheme();

  const handleValueChange = (type: "increment" | "decrement") => {
    if (disabled) return;
    const intValue = parseInt(value);
    if (type === "increment") {
      if (maxValue && intValue + 1 > maxValue) return;
      onValueChange(`${intValue + 1}`);
    } else {
      if (minValue && intValue - 1 < minValue) return;

      if (intValue > 0) {
        onValueChange(`${intValue - 1}`);
      }
    }
  };
  return (
    <Stack
      direction="row"
      sx={{
        height: "31px",
        border: `1px solid ${theme.palette.neutral["020"]}`,
        borderRadius: "4px",
        overflow: "clip",
        width: "fit-content",
        backgroundColor: theme.palette.darkBg.high,
        paddingX: "4px",
      }}
    >
      <Stack
        onClick={(e) => {
          e.stopPropagation();
          handleValueChange("decrement");
        }}
        justifyContent={"center"}
        alignItems={"center"}
        sx={{
          borderRight: `1px solid ${theme.palette.neutral["020"]}`,
          cursor:
            disabled || value === `${minValue}` ? "not-allowed" : "pointer",
          aspectRatio: "1/1",
        }}
      >
        <MinusIcon
          sx={{
            color:
              disabled || value === "0" || value === `${minValue}`
                ? theme.palette.neutral["020"]
                : "inherit",
          }}
        />
      </Stack>
      <TextField
        inputProps={{
          inputMode: "numeric",
          pattern: "[0-9]*",
          min: 0,
          max: maxValue,
          onKeyDown: (e) => {
            if (
              e.key === "e" ||
              e.key === "E" ||
              e.key === "+" ||
              e.key === "-" ||
              (e.key === "." && isInteger) ||
              e.key === ","
            ) {
              e.preventDefault();
            }
          },
        }}
        type="number"
        value={value}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
        }}
        onBlur={(e) => {
          let value = Number(e.target.value);
          if (minValue) {
            value = Math.max(0, Math.max(minValue || 1, value));
          }
          onValueChange(value.toString());
        }}
        onChange={(e) => {
          e.stopPropagation();
          if (!disabled) {
            let value = Number(e.target.value);

            if (
              value.toString().startsWith("0") &&
              value.toString().length > 1
            ) {
              value = Number(value.toString().slice(1));
            }

            if (maxValue) {
              value = Math.max(0, Math.min(maxValue || 1, value));
            }

            // maximum 2 digits after decimal
            if (
              !isInteger &&
              value.toString().includes(".") &&
              value.toString().split(".")[1].length > 2
            ) {
              return;
            }
            onValueChange(value.toString());
          }
        }}
        sx={{
          paddingX: "0px",
          width: "40px",
          outline: "none",
          border: "none",
          outlineWidth: "0px",
          backgroundColor: theme.palette.neutral["005"],
          justifyContent: "center",
          textAlign: "center",
          ...theme.typography["body-small"],

          "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
            { display: "none" },

          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              padding: "0px",
              border: "none",
            },
            "& .MuiInputBase-input": {
              padding: "0px",
              border: "none",
              textAlign: "center",
            },
          },
        }}
      ></TextField>
      <Stack
        onClick={(e) => {
          e.stopPropagation();
          handleValueChange("increment");
        }}
        justifyContent={"center"}
        alignItems={"center"}
        sx={{
          borderLeft: `1px solid ${theme.palette.neutral["020"]}`,
          cursor:
            disabled || value === `${maxValue}` ? "not-allowed" : "pointer",
          aspectRatio: "1/1",
        }}
      >
        <PlusIcon
          sx={{
            color:
              disabled || value === maxValue?.toString()
                ? theme.palette.neutral["020"]
                : "inherit",
          }}
        />
      </Stack>
    </Stack>
  );
};
